<template>
	<div class="flex gap-4 self-start">
		<AppButton variant="secondary" @click="addDataRequest('datakeeper')">
			{{ createDatakeeperRequestText }}
		</AppButton>
		<AppButton
			v-if="hdnDataRequestsEnabled"
			variant="secondary"
			@click="addDataRequest('hdn')"
		>
			Gegevens ophalen via HDN
		</AppButton>
	</div>

	<ul
		v-if="dataRequests.length > 0 || status === 'complete'"
		class="flex flex-col gap-4"
	>
		<li v-for="request in dataRequests" :key="request.id">
			<AppCard class="max-w-2xl">
				<DataRequestStatusCard
					:data-request="request"
					:validations="request.validations"
					:child="request.children[0]"
					:customer
				/>
			</AppCard>
		</li>
	</ul>
</template>

<script setup lang="ts">
import AppButton from "@/components/AppButton.vue";
import { useDossierStore } from "@/store/useDossierStore";
import { computed } from "vue";
import { useQuery, useZero } from "@/composables/useZero";
import { DateTime } from "luxon";
import type { InventarisationCustomer } from "@loanlink-nl/model-app";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import type { Row } from "@rocicorp/zero";
import { mutators, queries, type Schema } from "@loanlink-nl/zero";

const hdnDataRequestsEnabled = useFeatureFlag("hdn-datarequests-enabled");

const props = defineProps<{
	customer: InventarisationCustomer;
}>();

const dossierStore = useDossierStore();
const z = useZero();

const { data: dataRequests, status } = useQuery(() =>
	queries.dataRequests.forCustomer({
		dossierId: dossierStore.selectedDossier?.id ?? "",
		customerId: props.customer.id,
	}),
);

function hasActiveDataRequest(
	dataRequests: Readonly<Row<Schema["tables"]["dataRequestTable"]>[]>,
) {
	return (
		dataRequests.length > 0 && dataRequests.some((x) => x.status !== "failed")
	);
}

const createDatakeeperRequestText = computed(() => {
	if (hdnDataRequestsEnabled.value) {
		if (hasActiveDataRequest(dataRequests.value)) {
			return "Opnieuw gegevens ophalen via Datakeeper";
		}

		return "Gegevens ophalen via Datakeeper";
	}

	if (hasActiveDataRequest(dataRequests.value)) {
		return "Opnieuw gegevens ophalen";
	}

	return "Gegevens ophalen";
});

function addDataRequest(channel: "datakeeper" | "hdn") {
	if (!dossierStore.selectedDossier) {
		return;
	}

	if (channel === "hdn") {
		z.value.mutate(
			mutators.dataRequestTable.create({
				id: crypto.randomUUID(),
				dossierId: dossierStore.selectedDossier.id,
				agencyId: dossierStore.selectedDossier.agencyId,
				customerId: props.customer.id,
				hdnDossierId: crypto.randomUUID(),
				requestTraceNr: crypto.randomUUID(),
				channel,
				createdAt: DateTime.now().toMillis(),
			}),
		);
	} else if (channel === "datakeeper") {
		z.value.mutate(
			mutators.dataRequestTable.create({
				id: crypto.randomUUID(),
				dossierId: dossierStore.selectedDossier.id,
				agencyId: dossierStore.selectedDossier.agencyId,
				customerId: props.customer.id,
				channel,
				createdAt: DateTime.now().toMillis(),
			}),
		);
	}
}
</script>
